<template>
  <Container>
    <div class="ding-toolbar">
      <div class="ding-toolbar-left">
        <Button type="primary" :loading="loading" @click="loadOngoing">刷新进行中</Button>
        <Input
          v-model="query.conferenceId"
          clearable
          style="width:260px;"
          placeholder="会议 conferenceId"
          @on-enter="loadDetail"
        />
        <Input
          v-model="query.userid"
          clearable
          style="width:160px;"
          placeholder="userid（查录制用）"
        />
        <Button :loading="detailLoading" :disabled="!query.conferenceId" @click="loadDetail">查录制/回放</Button>
        <Input
          v-model="query.scheduleId"
          clearable
          style="width:220px;"
          placeholder="预约 scheduleConferenceId"
          @on-enter="loadSchedule"
        />
        <Button :loading="scheduleLoading" :disabled="!query.scheduleId" @click="loadSchedule">查历史场次</Button>
      </div>
      <div class="ding-toolbar-right">
        <span class="ding-muted">进行中 {{ rows.length }} 场</span>
      </div>
    </div>

    <Alert v-if="hint" type="info" show-icon style="margin:8px 12px 0;">{{ hint }}</Alert>

    <div class="ding-table-wrap">
      <BaseTable
        :columns="columns"
        :data="rows"
        :loading="loading"
        border
      />
    </div>

    <Drawer v-model="detail.visible" title="会议与云录制" width="720" :mask-closable="true">
      <div v-if="detail.data">
        <div class="ding-info-grid">
          <div class="ding-info-item"><label>标题</label><span>{{ confTitle }}</span></div>
          <div class="ding-info-item"><label>会议 ID</label><span>{{ detail.data.conferenceId }}</span></div>
          <div class="ding-info-item"><label>会议号</label><span>{{ conf.roomCode || '-' }}</span></div>
          <div class="ding-info-item"><label>状态</label><span>{{ statusText(conf.status) }}</span></div>
          <div class="ding-info-item"><label>开始</label><span>{{ formatTime(conf.startTime) }}</span></div>
          <div class="ding-info-item"><label>结束</label><span>{{ formatTime(conf.endTime) }}</span></div>
          <div class="ding-info-item"><label>云录制</label><span>{{ cloudRecordText(conf.cloudRecordStatus) }}</span></div>
          <div class="ding-info-item"><label>创建人</label><span>{{ conf.creatorNick || conf.creatorId || '-' }}</span></div>
          <div class="ding-info-item" v-if="conf.externalLinkUrl">
            <label>入会链接</label>
            <a :href="conf.externalLinkUrl" target="_blank">打开</a>
          </div>
        </div>

        <Alert
          v-for="(w, i) in (detail.data.warnings || [])"
          :key="'w'+i"
          type="warning"
          show-icon
          style="margin-top:12px;"
        >{{ w }}</Alert>

        <h4 style="margin:18px 0 10px;">历史视频（云录制）</h4>
        <BaseEmpty v-if="!(detail.data.videos || []).length" msg="暂无云录制（需会中开启录制）" />
        <div v-else class="ding-video-list">
          <div v-for="(v, idx) in detail.data.videos" :key="v.mediaId || idx" class="ding-video-card">
            <div class="ding-video-meta">
              <div><b>片段 {{ idx + 1 }}</b> · {{ formatDuration(v.playDuration || v.duration) }} · {{ formatSize(v.playSize || v.fileSize) }}</div>
              <div class="ding-muted">{{ formatTime(v.startTime) }} ~ {{ formatTime(v.endTime) }}</div>
              <div class="ding-muted" v-if="v.mediaId">mediaId: {{ v.mediaId }}</div>
              <div style="color:var(--error,#ed4014);" v-if="v.playError">{{ v.playError }}</div>
            </div>
            <div class="ding-video-actions">
              <Button
                v-if="v.mp4FileUrl"
                type="primary"
                size="small"
                @click="playVideo(v.mp4FileUrl, 'mp4')"
              >播放 MP4</Button>
              <Button
                v-if="v.playUrl"
                size="small"
                @click="playVideo(v.playUrl, 'hls')"
              >打开流地址</Button>
              <a v-if="v.mp4FileUrl" :href="v.mp4FileUrl" target="_blank" style="margin-left:8px;">下载</a>
            </div>
            <video
              v-if="player.url && player.key === (v.mediaId || idx) && player.type === 'mp4'"
              class="ding-player"
              :src="player.url"
              controls
              autoplay
            />
          </div>
        </div>
      </div>
    </Drawer>

    <Drawer v-model="schedule.visible" title="预约会历史场次" width="640">
      <BaseTable :columns="scheduleColumns" :data="schedule.list" :loading="scheduleLoading" border />
    </Drawer>

    <BaseLoadingModal v-model="loading" title="正在加载会议..." />
  </Container>
</template>

<script>
import Container from '@/components/app/container.vue';
import moment from 'moment';

export default {
  name: 'DingConferences',
  components: {Container},
  data() {
    return {
      loading: false,
      detailLoading: false,
      scheduleLoading: false,
      rows: [],
      hint: '列表仅含企业「进行中」会议；历史视频请输入 conferenceId 查云录制，或用预约 ID 查历史场次。',
      query: {
        conferenceId: '',
        userid: '',
        scheduleId: '',
      },
      detail: {
        visible: false,
        data: null,
      },
      schedule: {
        visible: false,
        list: [],
      },
      player: {
        key: '',
        url: '',
        type: '',
      },
    };
  },
  computed: {
    conf() {
      return (this.detail.data && this.detail.data.conf) || {};
    },
    confTitle() {
      return this.conf.title || '-';
    },
    columns() {
      return [{
        type: 'index',
        width: 50,
      }, {
        title: '标题',
        key: 'title',
        minWidth: 180,
      }, {
        title: '会议号',
        key: 'roomCode',
        width: 120,
      }, {
        title: '创建人',
        key: 'creatorNick',
        width: 120,
        render: (h, {row}) => h('span', row.creatorNick || row.creatorId || '-'),
      }, {
        title: '开始时间',
        key: 'startTime',
        width: 160,
        render: (h, {row}) => h('span', this.formatTime(row.startTime)),
      }, {
        title: '状态',
        key: 'status',
        width: 90,
        render: (h, {row}) => h('Tag', {props: {color: 'blue'}}, this.statusText(row.status)),
      }, {
        title: '操作',
        width: 160,
        fixed: 'right',
        render: (h, {row}) => h('div', {class: 'flex-wrap flex-center split5'}, [
          h('Button', {
            props: {size: 'small', type: 'primary'},
            on: {
              click: () => {
                this.query.conferenceId = row.conferenceId;
                this.loadDetail();
              },
            },
          }, '录制/回放'),
          h('Button', {
            props: {size: 'small'},
            on: {
              click: () => {
                this.query.conferenceId = row.conferenceId;
                this.$Message.info(`已填入 ${row.conferenceId}`);
              },
            },
          }, '填入 ID'),
        ]),
      }];
    },
    scheduleColumns() {
      return [{
        title: '标题',
        key: 'title',
        minWidth: 160,
      }, {
        title: 'conferenceId',
        key: 'conferenceId',
        minWidth: 200,
      }, {
        title: '状态',
        key: 'status',
        width: 90,
        render: (h, {row}) => h('span', this.statusText(row.status)),
      }, {
        title: '开始',
        key: 'startTime',
        width: 150,
        render: (h, {row}) => h('span', this.formatTime(row.startTime)),
      }, {
        title: '操作',
        width: 100,
        render: (h, {row}) => h('Button', {
          props: {size: 'small', type: 'primary'},
          on: {
            click: () => {
              this.query.conferenceId = row.conferenceId;
              this.schedule.visible = false;
              this.loadDetail();
            },
          },
        }, '查录制'),
      }];
    },
  },
  mounted() {
    this.$api.afterLogin(() => this.loadOngoing());
  },
  methods: {
    unwrap(res) {
      return (res && res.data && res.data.data) || (res && res.data) || {};
    },
    formatTime(t) {
      if (!t) return '-';
      const m = moment(Number(t) || t);
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(t);
    },
    formatDuration(v) {
      if (v == null || v === '') return '-';
      let sec = Number(v);
      if (!Number.isFinite(sec)) return String(v);
      // 钉钉文档：有时是毫秒
      if (sec > 100000) sec = Math.round(sec / 1000);
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = Math.floor(sec % 60);
      if (h) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    formatSize(n) {
      const v = Number(n);
      if (!Number.isFinite(v) || v <= 0) return '-';
      if (v < 1024) return `${v} B`;
      if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
      return `${(v / 1024 / 1024).toFixed(1)} MB`;
    },
    statusText(s) {
      const map = {0: '待开始', 1: '已结束', 2: '进行中'};
      return map[s] != null ? map[s] : (s != null ? String(s) : '-');
    },
    cloudRecordText(s) {
      const map = {0: '未录制', 1: '录制中', 2: '录制暂停', 3: '录制已结束'};
      return map[s] != null ? map[s] : (s != null ? String(s) : '-');
    },
    playVideo(url, type) {
      if (!url) return;
      if (type === 'hls') {
        window.open(url, '_blank');
        return;
      }
      const videos = (this.detail.data && this.detail.data.videos) || [];
      const hit = videos.find((v) => v.mp4FileUrl === url);
      this.player = {
        key: (hit && hit.mediaId) || url,
        url,
        type: 'mp4',
      };
    },
    async loadOngoing() {
      this.loading = true;
      try {
        const res = await this.$api.get('/ding/conferences?all=1');
        const data = this.unwrap(res);
        this.rows = Array.isArray(data.list) ? data.list : [];
      } catch (e) {
        this.Error(e);
      } finally {
        this.loading = false;
      }
    },
    async loadDetail() {
      const id = (this.query.conferenceId || '').trim();
      if (!id) {
        this.$Message.warning('请输入 conferenceId');
        return;
      }
      this.detailLoading = true;
      this.player = {key: '', url: '', type: ''};
      try {
        const params = ['withVideos=1', 'withPlay=1'];
        if (this.query.userid) params.push(`userid=${encodeURIComponent(this.query.userid)}`);
        const res = await this.$api.get(`/ding/conferences/${encodeURIComponent(id)}?${params.join('&')}`);
        this.detail.data = this.unwrap(res);
        this.detail.visible = true;
      } catch (e) {
        this.Error(e);
      } finally {
        this.detailLoading = false;
      }
    },
    async loadSchedule() {
      const id = (this.query.scheduleId || '').trim();
      if (!id) {
        this.$Message.warning('请输入 scheduleConferenceId');
        return;
      }
      this.scheduleLoading = true;
      try {
        const res = await this.$api.get(`/ding/conferences?scheduleConferenceId=${encodeURIComponent(id)}`);
        const data = this.unwrap(res);
        this.schedule.list = Array.isArray(data.list) ? data.list : [];
        this.schedule.visible = true;
      } catch (e) {
        this.Error(e);
      } finally {
        this.scheduleLoading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.ding-toolbar {
  flex-shrink: 0;
  box-sizing: border-box;
  min-height: 50px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}
.ding-toolbar-left,
.ding-toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.ding-muted {
  color: var(--text2);
  font-size: 12px;
  word-break: break-all;
}
.ding-table-wrap {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow: hidden;
  position: relative;
}
.ding-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.ding-info-item {
  display: flex;
  label {
    width: 80px;
    color: var(--text2);
    flex-shrink: 0;
  }
  span {
    word-break: break-all;
  }
}
.ding-video-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ding-video-card {
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
}
.ding-video-actions {
  margin-top: 8px;
}
.ding-player {
  margin-top: 10px;
  width: 100%;
  max-height: 360px;
  background: #000;
}
</style>
